import { Suspense, lazy, type ReactNode } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import type { Permission } from '@sarha/shared';
import { useAuth } from '../lib/auth';
import { AppLayout, FullPageLoader } from './AppLayout';
import { landingPath } from './navigation';
import { EmptyState } from '../components/ui';
import { ErrorBoundary } from '../components/ErrorBoundary';

const HomePage = lazy(() => import('../features/home/HomePage'));
const LoginPage = lazy(() => import('../features/auth/LoginPage'));
const DashboardPage = lazy(() => import('../features/dashboard/DashboardPage'));
const EmployeesPage = lazy(() => import('../features/employees/EmployeesPage'));
const EmployeeProfilePage = lazy(() => import('../features/employees/EmployeeProfilePage'));
const VehiclesPage = lazy(() => import('../features/vehicles/VehiclesPage'));
const VehicleDetailPage = lazy(() => import('../features/vehicles/VehicleDetailPage'));
const DailyEntryPage = lazy(() => import('../features/daily/DailyEntryPage'));
const PayrollPage = lazy(() => import('../features/payroll/PayrollPage'));
const PayslipPage = lazy(() => import('../features/payroll/PayslipPage'));
const ExpensesPage = lazy(() => import('../features/expenses/ExpensesPage'));
const CategoriesPage = lazy(() => import('../features/expenses/CategoriesPage'));
const SuppliersPage = lazy(() => import('../features/expenses/SuppliersPage'));
const RevenuesPage = lazy(() => import('../features/revenues/RevenuesPage'));
const ReportsPage = lazy(() => import('../features/reports/ReportsPage'));
const SettingsPage = lazy(() => import('../features/settings/SettingsPage'));
const UsersPage = lazy(() => import('../features/users/UsersPage'));
const AuditPage = lazy(() => import('../features/audit/AuditPage'));
const FiscalPage = lazy(() => import('../features/fiscal/FiscalPage'));
const ImportPage = lazy(() => import('../features/import/ImportPage'));
const AccountPage = lazy(() => import('../features/auth/AccountPage'));
const RenewalsPage = lazy(() => import('../features/renewals/RenewalsPage'));
const CustodyPage = lazy(() => import('../features/custody/CustodyPage'));
const TargetPage = lazy(() => import('../features/payroll/TargetPage'));
const PermissionsPage = lazy(() => import('../features/permissions/PermissionsPage'));

function Protected({ children, permission }: { children: ReactNode; permission?: Permission }): JSX.Element {
  const { isAuthenticated, isLoading, can, mustChangePassword } = useAuth();
  const location = useLocation();

  if (isLoading) return <FullPageLoader />;
  if (!isAuthenticated) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  // كلمة مرور أولية ⇒ لا تُفتح أي شاشة قبل تغييرها (الخادم يمنع أي عملية كذلك)
  if (mustChangePassword) return <AccountPage forced />;
  if (permission && !can(permission)) {
    return (
      <EmptyState
        title="لا تملك صلاحية الوصول"
        description="هذه الشاشة غير متاحة لدورك الحالي. راجع سوبر أدمن إذا كنت تحتاج الصلاحية."
      />
    );
  }
  return <>{children}</>;
}

/** حاجز أخطاء للصفحة يُصفَّر عند تغيّر المسار — فتبقى القائمة وتتعافى الصفحة بالتنقّل */
function RouteErrorBoundary({ children }: { children: ReactNode }): JSX.Element {
  const location = useLocation();
  return (
    <ErrorBoundary inline resetKey={location.pathname}>
      {children}
    </ErrorBoundary>
  );
}

/** «/» ⇒ أول شاشة يملكها الدور (لوحة المعلومات للأغلب، والتقارير لمطّلع التقارير) */
function LandingRedirect(): JSX.Element {
  const { can } = useAuth();
  return <Navigate to={landingPath(can)} replace />;
}

export function AppRouter(): JSX.Element {
  return (
    <Suspense fallback={<FullPageLoader />}>
      <Routes>
        {/* الصفحة الرئيسية العامة — قبل المسار المحمي */}
        <Route path="/" element={<HomePage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route
          path="/*"
          element={
            <Protected>
              <AppLayout>
                <RouteErrorBoundary>
                <Suspense fallback={<FullPageLoader />}>
                  <Routes>
                    <Route path="/" element={<LandingRedirect />} />
                    <Route path="/account" element={<AccountPage />} />
                    <Route
                      path="/payroll-target"
                      element={
                        <Protected permission="payroll.view">
                          <TargetPage />
                        </Protected>
                      }
                    />
                    <Route
                      path="/renewals"
                      element={
                        <Protected permission="employees.view">
                          <RenewalsPage />
                        </Protected>
                      }
                    />
                    <Route
                      path="/custody"
                      element={
                        <Protected permission="employees.view">
                          <CustodyPage />
                        </Protected>
                      }
                    />
                    <Route
                      path="/permissions"
                      element={
                        <Protected permission="users.manage">
                          <PermissionsPage />
                        </Protected>
                      }
                    />
                    <Route
                      path="/dashboard"
                      element={
                        <Protected permission="dashboard.view">
                          <DashboardPage />
                        </Protected>
                      }
                    />
                    <Route
                      path="/daily"
                      element={
                        <Protected permission="daily.view">
                          <DailyEntryPage />
                        </Protected>
                      }
                    />
                    <Route
                      path="/employees"
                      element={
                        <Protected permission="employees.view">
                          <EmployeesPage />
                        </Protected>
                      }
                    />
                    <Route
                      path="/employees/:id"
                      element={
                        <Protected permission="employees.view">
                          <EmployeeProfilePage />
                        </Protected>
                      }
                    />
                    <Route
                      path="/vehicles"
                      element={
                        <Protected permission="vehicles.view">
                          <VehiclesPage />
                        </Protected>
                      }
                    />
                    <Route
                      path="/vehicles/:id"
                      element={
                        <Protected permission="vehicles.view">
                          <VehicleDetailPage />
                        </Protected>
                      }
                    />
                    <Route
                      path="/payroll"
                      element={
                        <Protected permission="payroll.view">
                          <PayrollPage />
                        </Protected>
                      }
                    />
                    <Route
                      path="/payroll/:period/:employeeId"
                      element={
                        <Protected permission="payroll.view">
                          <PayslipPage />
                        </Protected>
                      }
                    />
                    <Route
                      path="/expenses"
                      element={
                        <Protected permission="expenses.view">
                          <ExpensesPage />
                        </Protected>
                      }
                    />
                    <Route
                      path="/expense-categories"
                      element={
                        <Protected permission="catalog.view">
                          <CategoriesPage />
                        </Protected>
                      }
                    />
                    <Route
                      path="/suppliers"
                      element={
                        <Protected permission="catalog.view">
                          <SuppliersPage />
                        </Protected>
                      }
                    />
                    <Route
                      path="/revenues"
                      element={
                        <Protected permission="revenues.view">
                          <RevenuesPage />
                        </Protected>
                      }
                    />
                    <Route
                      path="/reports"
                      element={
                        <Protected permission="reports.view">
                          <ReportsPage />
                        </Protected>
                      }
                    />
                    <Route
                      path="/fiscal"
                      element={
                        <Protected permission="fiscal.view">
                          <FiscalPage />
                        </Protected>
                      }
                    />
                    <Route
                      path="/settings"
                      element={
                        <Protected permission="settings.view">
                          <SettingsPage />
                        </Protected>
                      }
                    />
                    <Route
                      path="/users"
                      element={
                        <Protected permission="users.view">
                          <UsersPage />
                        </Protected>
                      }
                    />
                    <Route
                      path="/import"
                      element={
                        <Protected permission="data.import">
                          <ImportPage />
                        </Protected>
                      }
                    />
                    <Route
                      path="/audit"
                      element={
                        <Protected permission="audit.view">
                          <AuditPage />
                        </Protected>
                      }
                    />
                    <Route
                      path="*"
                      element={<EmptyState title="الصفحة غير موجودة" description="تأكد من الرابط أو ارجع للصفحة الرئيسية." />}
                    />
                  </Routes>
                </Suspense>
                </RouteErrorBoundary>
              </AppLayout>
            </Protected>
          }
        />
      </Routes>
    </Suspense>
  );
}
